import { FC, FormEvent, FormEventHandler, useState } from "react";
import {
  Button,
  Flex,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
} from "@chakra-ui/react";
import { AsyncTask } from "../domain";
import { sendPhoneVerificationCode, verifyPhoneCode } from "../adapters/api";
import { useWeb3ProviderContext } from "../contexts/Web3Provider";

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: (isCodeVerified: boolean) => void;
}

export const PhoneVerificationModal: FC<PhoneVerificationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { connectedProvider } = useWeb3ProviderContext();
  const [phoneNumberPrefix, setPhoneNumberPrefix] = useState<string>("1");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [codeSending, setCodeSending] = useState<AsyncTask<null, string>>({
    status: "pending",
  });
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [codeVerification, setCodeVerification] = useState<
    AsyncTask<null, string>
  >({
    status: "pending",
  });

  const onSendVerificationCode = (e: FormEvent) => {
    e.preventDefault();

    if (connectedProvider.status === "successful") {
      setCodeSending({ status: "loading" });
      sendPhoneVerificationCode({
        ethereumAddress: connectedProvider.data.address,
        phone: `${phoneNumberPrefix}${phoneNumber}`,
      })
        .then(() => {
          setCodeSending({ status: "successful", data: null });
        })
        .catch((err) => {
          setCodeSending({ status: "failed", error: err.message });
        });
    }
  };

  const onVerifyCode = (e: FormEvent) => {
    e.preventDefault();

    if (connectedProvider.status === "successful") {
      setCodeVerification({ status: "loading" });
      verifyPhoneCode({
        ethereumAddress: connectedProvider.data.address,
        phone: `${phoneNumberPrefix}${phoneNumber}`,
        code: verificationCode,
      })
        .then(() => {
          setCodeVerification({ status: "successful", data: null });
          onResetAndClose(true);
        })
        .catch((err) => {
          setCodeVerification({ status: "failed", error: err.message });
        });
    }
  };

  const onResetAndClose = (isCodeVerified: boolean) => {
    setPhoneNumberPrefix("1");
    setPhoneNumber("");
    setCodeSending({ status: "pending" });
    setVerificationCode("");
    onClose(isCodeVerified);
  };

  return (
    <Modal isOpen={isOpen} isCentered onClose={() => onResetAndClose(false)}>
      <ModalOverlay />
      <ModalContent width="640px">
        <ModalCloseButton size="lg" margin="2" />
        {codeSending.status === "pending" ||
        codeSending.status === "loading" ? (
          <ModalBody>
            <Flex direction="column" align="center">
              <Heading size="lg" marginTop="16">
                Verify phone
              </Heading>
              <Text fontSize="xl" marginTop="6" textAlign="center">
                Enter the phone number to which the verification code will be
                sent.
                <br />
                (The phone number will not be linked to your Ethereum address).
              </Text>
              <form style={{ width: "100%" }} onSubmit={onSendVerificationCode}>
                <Flex direction="column" align="center">
                  <Select
                    width="75%"
                    size="lg"
                    value={phoneNumberPrefix}
                    marginTop="8"
                    onChange={(e) => setPhoneNumberPrefix(e.target.value)}
                  >
                    <option value="+1">+1 United States</option>
                    <option value="+34">+34 Spain</option>
                    <option value="+41">+41 Switzerland</option>
                    <option value="+91">+91 India</option>
                    <option value="+351">+351 Portugal</option>
                  </Select>
                  <Input
                    width="75%"
                    marginTop="4"
                    size="lg"
                    placeholder="Your phone number"
                    value={phoneNumber}
                    autoFocus
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  <Button
                    type="submit"
                    marginTop="8"
                    marginBottom="8"
                    size="lg"
                    colorScheme="primary"
                    isDisabled={!phoneNumber}
                    isLoading={codeSending.status === "loading"}
                  >
                    Send verification code
                  </Button>
                </Flex>
              </form>
            </Flex>
          </ModalBody>
        ) : (
          <ModalBody>
            <Flex direction="column" align="center">
              <Heading size="lg" marginTop="16">
                Verify phone
              </Heading>
              <Text fontSize="xl" marginTop="6" textAlign="center">
                We sent you a code.
              </Text>
              <form style={{ width: "100%" }} onSubmit={onVerifyCode}>
                <Flex direction="column" align="center">
                  <Input
                    width="75%"
                    marginTop="8"
                    size="lg"
                    placeholder="Verification code"
                    value={verificationCode}
                    autoFocus
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                  <Button
                    type="submit"
                    marginTop="8"
                    marginBottom="8"
                    size="lg"
                    colorScheme="primary"
                    isDisabled={!verificationCode}
                    isLoading={codeVerification.status === "loading"}
                  >
                    Verify
                  </Button>
                </Flex>
              </form>
            </Flex>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};
